import React, { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InputAdornment from '@mui/material/InputAdornment';
import { resolveGroupsAssetUrl } from '../../../api/httpClientGroups';
import {
  updateMeetingAttendanceStatus,
  setMeetingAttendanceExempt,
  updateMeetingGuestStatus,
  removeMeetingAttendance,
  removeMeetingGuest,
  fetchMeetingAttendanceDetail,
  syncAllMeetingMembers,
  fetchMeetingAttendances,
  unregisterMeetingAttendance,
} from '../../../controllers/meetingsController';
import { toast } from 'react-toastify';
import AddMeetingAttendeeDialog from './AddMeetingAttendeeDialog';

const STATUS_EXEMPT = 0;   // İzinli (muafiyet)
const STATUS_PENDING = 1;  // Beklemede/Kayıt olmuş
const STATUS_ATTENDED = 2; // Katıldı
const STATUS_ABSENT = 3;   // Gelmedi
const STATUS_CANCELED = 4; // Kayıt iptal edildi

const statusLabels = {
  [STATUS_EXEMPT]: 'İzinli',
  [STATUS_PENDING]: 'Beklemede',
  [STATUS_ATTENDED]: 'Katıldı',
  [STATUS_ABSENT]: 'Gelmedi',
  [STATUS_CANCELED]: 'İptal',
};

const MeetingAttendanceTable = ({
  meetingId,
  allowGuests,
  attendanceData,
  onRefresh,
  reportExists,
  reportLoading,
  excelLoading,
  onViewReport,
  onDownloadExcel,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteAttendeeDialog, setDeleteAttendeeDialog] = useState({ open: false, item: null });
  const [deleteGuestDialog, setDeleteGuestDialog] = useState({ open: false, guest: null, hostName: '' });
  const [detailDialog, setDetailDialog] = useState({ open: false, data: null, loading: false });
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredAttendances, setFilteredAttendances] = useState(null);
  const [unregisterLoading, setUnregisterLoading] = useState(null); // attendanceId

  const attendances = filteredAttendances || attendanceData?.attendances || [];
  const guestGroups = attendanceData?.guestGroups || [];

  // Arama için debounce
  useEffect(() => {
    if (!meetingId) return;
    if (!searchTerm || searchTerm.trim().length === 0) {
      setFilteredAttendances(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await fetchMeetingAttendances(meetingId, { searchTerm: searchTerm.trim() });
        setFilteredAttendances(results || []);
      } catch (e) {
        toast.error(e?.message || 'Arama yapılamadı');
        setFilteredAttendances([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [meetingId, searchTerm]);

  // attendanceData değiştiğinde arama sonuçlarını temizle
  useEffect(() => {
    if (!searchTerm) {
      setFilteredAttendances(null);
    }
  }, [attendanceData, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredAttendances(null);
  };

  const getGuestsForAttendance = (personId) => {
    const group = guestGroups.find((g) => (g.hostPersonId || g.HostPersonId) === personId);
    return group?.guests || [];
  };

  const handleStatusChange = async (attendance, newStatus) => {
    if (!meetingId || !attendance?.id) return;
    try {
      await updateMeetingAttendanceStatus({
        meetingId,
        attendanceId: attendance.id,
        status: newStatus,
      });
      toast.success('Durum güncellendi');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.message || 'Durum güncellenemedi');
    }
  };

  const handleSetExempt = async (attendance, isExempt) => {
    if (!meetingId || !attendance?.id) return;
    try {
      await setMeetingAttendanceExempt({
        meetingId,
        attendanceId: attendance.id,
        isExempt,
      });
      toast.success(isExempt ? 'İzin verildi' : 'İzin kaldırıldı');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.message || 'İzin durumu güncellenemedi');
    }
  };

  const handleGuestStatusChange = async (guest, newStatus) => {
    if (!meetingId || !guest?.id) return;
    try {
      await updateMeetingGuestStatus({
        meetingId,
        guestId: guest.id,
        status: newStatus,
      });
      toast.success('Misafir durumu güncellendi');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.message || 'Misafir durumu güncellenemedi');
    }
  };

  const handleRemoveAttendee = async () => {
    const item = deleteAttendeeDialog.item;
    if (!meetingId || !item?.id) return;
    try {
      await removeMeetingAttendance(meetingId, item.id);
      toast.success('Katılımcı listeden kaldırıldı');
      setDeleteAttendeeDialog({ open: false, item: null });
      onRefresh?.();
    } catch (e) {
      toast.error(e?.message || 'Katılımcı kaldırılamadı');
    }
  };

  const handleRemoveGuest = async () => {
    const guest = deleteGuestDialog.guest;
    if (!meetingId || !guest?.id) return;
    try {
      await removeMeetingGuest(meetingId, guest.id);
      toast.success('Misafir kaldırıldı');
      setDeleteGuestDialog({ open: false, guest: null, hostName: '' });
      onRefresh?.();
    } catch (e) {
      toast.error(e?.message || 'Misafir kaldırılamadı');
    }
  };

  const toggleExpand = (attendance) => {
    const id = attendance?.id;
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleSyncAllMembers = async () => {
    if (!meetingId) return;
    setSyncLoading(true);
    try {
      const syncedCount = await syncAllMeetingMembers(meetingId);
      toast.success(`Toplantıya ${syncedCount ?? 0} üye senkronize edildi`);
      setSearchTerm('');
      setFilteredAttendances(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.message || 'Üyeler senkronize edilemedi');
    } finally {
      setSyncLoading(false);
    }
  };

  const openDetailDialog = async (attendance) => {
    if (!meetingId || !attendance?.id) return;
    setDetailDialog({ open: true, data: null, loading: true });
    try {
      const data = await fetchMeetingAttendanceDetail(meetingId, attendance.id);
      setDetailDialog((prev) => ({ ...prev, data, loading: false }));
    } catch (e) {
      toast.error(e?.message || 'Detay alınamadı');
      setDetailDialog((prev) => ({ ...prev, data: null, loading: false }));
    }
  };

  const closeDetailDialog = () => {
    setDetailDialog({ open: false, data: null, loading: false });
  };

  const handleUnregister = async (attendance) => {
    if (!meetingId || !attendance?.id) return;
    setUnregisterLoading(attendance.id);
    try {
      await unregisterMeetingAttendance({
        meetingId,
        attendanceId: attendance.id,
      });
      toast.success('Kayıt iptal edildi');
      onRefresh?.();
    } catch (e) {
      toast.error(e?.message || 'Kayıt iptal edilemedi');
    } finally {
      setUnregisterLoading(null);
    }
  };

  const tableCellHeadSx = {
    fontSize: '14px',
    fontWeight: 500,
    borderBottom: '1px dashed #e8e8e8',
    px: 1.5,
    py: 1.5,
    color: '#a3a3a3',
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
    },
    '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
  };

  return (
    <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444' }}>
          Katılımcılar
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {reportExists && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={reportLoading ? <CircularProgress size={16} color="inherit" /> : <VisibilityIcon sx={{ width: 16 }} />}
                onClick={onViewReport}
                disabled={reportLoading || excelLoading}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  backgroundColor: '#E7EDF4',
                  boxShadow: 'none',
                  color: '#0E4D94',
                  '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
                }}
              >
                Raporu Görüntüle
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={excelLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon sx={{ width: 16 }} />}
                onClick={onDownloadExcel}
                disabled={excelLoading}
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': { borderColor: '#0E4D94', color: '#0E4D94', backgroundColor: 'rgba(14, 77, 148, 0.04)' },
                }}
              >
                Excel İndir
              </Button>
            </>
          )}
          <Button
            variant="outlined"
            size="small"
            disabled={syncLoading}
            onClick={handleSyncAllMembers}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              borderColor: '#e6e6e6',
              color: '#444444',
              '&:hover': { borderColor: '#0E4D94', color: '#0E4D94', backgroundColor: 'rgba(14, 77, 148, 0.04)' },
            }}
          >
            {syncLoading ? 'Senkronize ediliyor...' : 'Üyeleri Senkronize Et'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ width: 16 }} />}
            onClick={() => setAddDialogOpen(true)}
            sx={{
              textTransform: 'none',
              backgroundColor: '#E7EDF4',
              boxShadow: 'none',
              fontSize: 13,
              borderRadius: '5px',
              color: '#0E4D94',
              '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
            }}
          >
            Katılımcı Ekle
          </Button>
        </Stack>
      </Stack>

      {/* Arama Çubuğu */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="İsim veya telefon numarası ile ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#666' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                {searchLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <IconButton size="small" onClick={handleClearSearch} sx={{ p: 0.5 }}>
                    <ClearIcon sx={{ fontSize: 18, color: '#666' }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={textFieldSx}
        />
      </Box>

      {/* Toplam katılımcı */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>
          Toplam <strong style={{ color: '#444444', fontWeight: 600 }}>{attendances.length}</strong> katılımcı
        </Typography>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'transparent' }}>
            <TableRow>
              <TableCell sx={{ ...tableCellHeadSx, width: 40 }} />
              <TableCell sx={tableCellHeadSx}>Ad Soyad</TableCell>
              <TableCell sx={tableCellHeadSx}>Tür</TableCell>
              <TableCell sx={tableCellHeadSx}>İzin</TableCell>
              <TableCell sx={tableCellHeadSx}>Durum</TableCell>
              <TableCell sx={tableCellHeadSx}>Check-in</TableCell>
              <TableCell sx={tableCellHeadSx}>Misafir</TableCell>
              <TableCell align="right" sx={tableCellHeadSx}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attendances.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                    Henüz katılımcı yok. Katılımcı eklemek için yukarıdaki butonu kullanın.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              attendances.map((att, attIndex) => {
                const guests = getGuestsForAttendance(att.personId);
                const hasGuests = allowGuests && (att.guestCount > 0 || guests.length > 0);
                const isExpanded = expandedId === att.id;
                const status = typeof att.status === 'number' ? att.status : att.status;

                return (
                  <React.Fragment key={att.id}>
                    <TableRow
                      hover
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: attIndex === attendances.length - 1 && !isExpanded ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                          color: '#444444',
                          fontSize: '14px',
                          fontWeight: 500,
                        },
                        '&:hover': { backgroundColor: '#fafafa' },
                      }}
                    >
                      <TableCell sx={{ width: 40 }}>
                        {hasGuests && (
                          <IconButton size="small" onClick={() => toggleExpand(att)}>
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={resolveGroupsAssetUrl(att.profilePictureUrl)}
                            sx={{ width: 36, height: 36, bgcolor: att.profilePictureUrl ? 'transparent' : '#E7EDF4', color: '#0E4D94', fontSize: 14, fontWeight: 600 }}
                          >
                            {!att.profilePictureUrl && (att.fullName?.charAt(0) || '?')}
                          </Avatar>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={500}>{att.fullName}</Typography>
                            {att.parentFullName && (
                              <Typography variant="caption" sx={{ color: '#888', fontSize: '12px' }}>Üye: {att.parentFullName}</Typography>
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={att.typeName || (att.type === 1 ? 'Yakın' : 'Üye')}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: att.type === 1 ? '#FFF4E6' : '#E7EDF4',
                            color: att.type === 1 ? '#FF9800' : '#0E4D94',
                            borderRadius: '12px',
                            border: 'none',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={att.isExempt ? 'İzinli' : 'Hayır'}
                          size="small"
                          onClick={() => handleSetExempt(att, !att.isExempt)}
                          sx={{
                            height: 24,
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: att.isExempt ? '#DFFFEA' : '#f5f5f5',
                            color: att.isExempt ? '#17C653' : '#666666',
                            border: 'none',
                            borderRadius: '12px',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={status}
                          onChange={(e) => handleStatusChange(att, Number(e.target.value))}
                          sx={{
                            minWidth: 120,
                            ...textFieldSx,
                            '& .MuiInputBase-root': { minHeight: 40 },
                          }}
                        >
                          <MenuItem value={STATUS_EXEMPT}>{statusLabels[STATUS_EXEMPT]}</MenuItem>
                          <MenuItem value={STATUS_PENDING}>{statusLabels[STATUS_PENDING]}</MenuItem>
                          <MenuItem value={STATUS_ATTENDED}>{statusLabels[STATUS_ATTENDED]}</MenuItem>
                          <MenuItem value={STATUS_ABSENT}>{statusLabels[STATUS_ABSENT]}</MenuItem>
                          <MenuItem value={STATUS_CANCELED}>{statusLabels[STATUS_CANCELED]}</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {att.checkedInAt ? new Date(att.checkedInAt).toLocaleString('tr-TR') : '-'}
                      </TableCell>
                      <TableCell>
                        {hasGuests ? (
                          <Chip
                            label={att.guestCount || guests.length}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: '#E7EDF4',
                              color: '#0E4D94',
                              borderRadius: '12px',
                              border: 'none',
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {status === STATUS_PENDING && (
                            <Tooltip title="Kayıt İptal">
                              <IconButton
                                size="small"
                                onClick={() => handleUnregister(att)}
                                disabled={unregisterLoading === att.id}
                                sx={{
                                  backgroundColor: '#FFF4E6',
                                  borderRadius: '6px',
                                  width: 32,
                                  height: 32,
                                  '&:hover': { backgroundColor: '#FFE0B2' },
                                  '& svg': { fontSize: '14px', color: '#FF9800' },
                                }}
                              >
                                {unregisterLoading === att.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteOutlineIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Detay">
                            <IconButton
                              size="small"
                              onClick={() => openDetailDialog(att)}
                              sx={{
                                backgroundColor: '#E7EDF4',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': { backgroundColor: '#CFDBEA' },
                                '& svg': { fontSize: '16px', color: '#0E4D94' },
                              }}
                            >
                              <InfoOutlinedIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Listeden kaldır">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteAttendeeDialog({ open: true, item: att })}
                              sx={{
                                backgroundColor: '#FFEEF3',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': { backgroundColor: '#FFD6E0' },
                                '& svg': { fontSize: '14px', color: '#F8285A' },
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {isExpanded && hasGuests && (
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell
                          colSpan={8}
                          sx={{
                            py: 2,
                            borderBottom: attIndex === attendances.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            borderTop: 'none',
                          }}
                        >
                          <Box sx={{ pl: 6 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '13px', fontWeight: 600, color: '#444444' }}>
                              Misafirler
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', py: 1 }}>Ad Soyad</TableCell>
                                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', py: 1 }}>Telefon</TableCell>
                                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', py: 1 }}>Durum</TableCell>
                                  <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', py: 1 }}>Check-in</TableCell>
                                  <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', py: 1 }}>İşlem</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {guests.map((g) => (
                                  <TableRow key={g.id} sx={{ '& .MuiTableCell-root': { borderBottom: '1px dashed #e8e8e8', py: 1, fontSize: '14px', color: '#444444' } }}>
                                    <TableCell>{g.fullName || `${g.firstName || ''} ${g.lastName || ''}`.trim()}</TableCell>
                                    <TableCell>{g.phoneNumber || '-'}</TableCell>
                                    <TableCell>
                                      <TextField
                                        select
                                        size="small"
                                        value={typeof g.status === 'number' ? g.status : STATUS_ABSENT}
                                        onChange={(e) => handleGuestStatusChange(g, Number(e.target.value))}
                                        sx={{
                                          minWidth: 110,
                                          ...textFieldSx,
                                          '& .MuiInputBase-root': { minHeight: 36 },
                                        }}
                                      >
                                        <MenuItem value={STATUS_PENDING}>{statusLabels[STATUS_PENDING]}</MenuItem>
                                        <MenuItem value={STATUS_ATTENDED}>{statusLabels[STATUS_ATTENDED]}</MenuItem>
                                        <MenuItem value={STATUS_ABSENT}>{statusLabels[STATUS_ABSENT]}</MenuItem>
                                      </TextField>
                                    </TableCell>
                                    <TableCell>{g.checkedInAt ? new Date(g.checkedInAt).toLocaleString('tr-TR') : '-'}</TableCell>
                                    <TableCell align="right">
                                      <IconButton
                                        size="small"
                                        onClick={() => setDeleteGuestDialog({ open: true, guest: g, hostName: att.fullName })}
                                        sx={{
                                          backgroundColor: '#FFEEF3',
                                          borderRadius: '6px',
                                          width: 32,
                                          height: 32,
                                          '&:hover': { backgroundColor: '#FFD6E0' },
                                          '& svg': { fontSize: '14px', color: '#F8285A' },
                                        }}
                                      >
                                        <DeleteOutlineIcon />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddMeetingAttendeeDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        meetingId={meetingId}
        allowGuests={allowGuests}
        onAdded={() => {
          setAddDialogOpen(false);
          onRefresh?.();
        }}
      />

      <Dialog open={deleteAttendeeDialog.open} onClose={() => setDeleteAttendeeDialog({ open: false, item: null })} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Katılımcıyı Kaldır
          <IconButton
            onClick={() => setDeleteAttendeeDialog({ open: false, item: null })}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            {deleteAttendeeDialog.item
              ? `"${deleteAttendeeDialog.item.fullName}" kişisini toplantı katılımcı listesinden kaldırmak istediğinize emin misiniz?`
              : 'Bu katılımcıyı kaldırmak istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setDeleteAttendeeDialog({ open: false, item: null })}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleRemoveAttendee}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#E01E4A', boxShadow: 'none' },
            }}
          >
            Kaldır
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteGuestDialog.open} onClose={() => setDeleteGuestDialog({ open: false, guest: null, hostName: '' })} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Misafiri Kaldır
          <IconButton
            onClick={() => setDeleteGuestDialog({ open: false, guest: null, hostName: '' })}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            {deleteGuestDialog.guest
              ? `"${deleteGuestDialog.guest.fullName || `${deleteGuestDialog.guest.firstName || ''} ${deleteGuestDialog.guest.lastName || ''}`.trim()}" misafirini kaldırmak istediğinize emin misiniz?`
              : 'Bu misafiri kaldırmak istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setDeleteGuestDialog({ open: false, guest: null, hostName: '' })}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleRemoveGuest}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#E01E4A', boxShadow: 'none' },
            }}
          >
            Kaldır
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailDialog.open} onClose={closeDetailDialog} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Katılımcı Detayı
          <IconButton
            onClick={closeDetailDialog}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {detailDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : detailDialog.data ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Ad Soyad</Typography>
                <Typography variant="body1" fontWeight={500}>{detailDialog.data.fullName ?? '-'}</Typography>
              </Box>
              {detailDialog.data.parentFullName && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Üye</Typography>
                  <Typography variant="body2">{detailDialog.data.parentFullName}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">Tür</Typography>
                <Typography variant="body2">{detailDialog.data.typeName ?? (detailDialog.data.type === 1 ? 'Yakın' : 'Üye')}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Durum</Typography>
                <Typography variant="body2">{statusLabels[Number(detailDialog.data.status)] ?? '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">İzinli</Typography>
                <Typography variant="body2">{detailDialog.data.isExempt ? 'Evet' : 'Hayır'}</Typography>
              </Box>
              {detailDialog.data.checkedInAt && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Check-in</Typography>
                  <Typography variant="body2">{new Date(detailDialog.data.checkedInAt).toLocaleString('tr-TR')}</Typography>
                </Box>
              )}
              {detailDialog.data.guests?.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Misafirler</Typography>
                  <Stack spacing={0.5}>
                    {detailDialog.data.guests.map((g) => (
                      <Typography key={g.id} variant="body2">
                        {g.fullName || `${g.firstName || ''} ${g.lastName || ''}`.trim()}
                        {g.phoneNumber ? ` · ${g.phoneNumber}` : ''}
                        {typeof g.status === 'number' ? ` · ${statusLabels[g.status] ?? '-'}` : ''}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          ) : (
            <Typography color="text.secondary">Detay yüklenemedi.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={closeDetailDialog}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MeetingAttendanceTable;
